import React, { useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { Link } from 'react-router-dom';
import * as queryString from 'query-string';
import { Box } from '@mui/material';

import { makeStyles, withStyles } from 'tss-react/mui';

import { useL10n } from '@uplift/hooks';
import { EMPTY_STRING, ROUTING_CONFIG } from '@uplift/constants';
import { Contact } from '@uplift/types';
import { SeePasswordButton, Button, Alert, TextInput, TextInputExtendedProps } from '@uplift/ui-v2';
import { authLogin, AuthLoginInput, AuthStatus } from '@uplift/api';
import { useLoginValidationSchema } from './validation-schema.hook';
import { LocalStorageService } from '@uplift/services';


type LoginFormProps = {
  onSubmit: (redirectUrl: string, code: string) => void;
  onCheckEmail: (email: string) => void;
  onMfaChallenge: (userId: string, email: string, contacts: Contact[], requestId: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  forgotPassword: {
    right: 0,
    paddingLeft: theme.spacing(2),
    top: '76px',
    marginTop: theme.spacing(1),
    '& a': {
      fontSize: '13px',
      lineHeight: '22px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      textDecoration: 'none',
      color: '#A568F3',
    },
  },
  error: {
    textAlign: 'left',
    color: theme.palette.error.main,
    margin: '3px 14px 0 0',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: 0,
  },
  form: {
    width: '100%',
    maxWidth: '510px',
    flex: 1,
    paddingBottom: theme.spacing(3)
  },
  errorAlert: {
    marginTop: '24px',
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    maxWidth: '432px',
  },
}));


export const AuthAlert = withStyles(Alert, theme => ({
  root: {
    padding: theme.spacing(1, 5),
  },
  title: {
    fontWeight: 900,
  },
}));


const PasswordInput: React.VFC<TextInputExtendedProps> = (props) => {
  const [ showPassword, setShowPassword ] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <TextInput
      type={showPassword ? 'text' : 'password'}
      endAdornment={(
        <SeePasswordButton
          show={showPassword}
          onClick={handleClickShowPassword}
        />
      )}
      {...props}
    />
  );
};

export type LoginFormType = {
  username: string;
  password: string;
};

const initialValues: LoginFormType = {
  username: EMPTY_STRING,
  password: EMPTY_STRING
};

export const LoginForm: React.FC<LoginFormProps> = props => {
  const [ error, setError ] = useState<string>();
  const { classes } = useStyles();
  const l10n = useL10n();
  const validationSchema = useLoginValidationSchema(l10n);

  const onSubmit = async ({ username, password }: LoginFormType, { resetForm }: FormikHelpers<LoginFormType>) => {
    const search = queryString.parse(window.location.search);
    const redirectTo = search?.redirect_to;

    const input: AuthLoginInput = {
      username,
      password,
      redirect_to: (redirectTo && typeof redirectTo === 'string') ? redirectTo : undefined,
      device_token: LocalStorageService.getItem('deviceToken')
    };

    try {
      const result = await authLogin(input);

      if ('error' in result) {
        throw new Error(result.error);
      }

      if (result.status === AuthStatus.REDIRECT) {
        return props.onSubmit(result.redirectUrl, result.code);
      }

      if (result.deviceToken) {
        LocalStorageService.setItem('deviceToken', result.deviceToken);
      }

      switch (result.status) {
        case AuthStatus.MFA_REQUIRED:
          return props.onCheckEmail(username);
        case AuthStatus.MFA_CHALLENGE:
          resetForm();
          return props.onMfaChallenge(result.id, result.email, result.contacts ?? [], result.requestId);
      }

    } catch (error) {
      setError(error?.message || 'unknown');
    }
  };

  const formChangeHandler = () => {
    if (error) {
      setError(undefined);
    }
  };

  const errorDetails = React.useMemo(() => {
    if (error) {
      return l10n.auth.errors[error]
        || l10n.auth.errors.unknown;
    }
  }, [ error, l10n.auth.errors ]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {formik => (
        <Form
          onSubmit={formik.handleSubmit}
          onChange={formChangeHandler}
          className={classes.form}
        >
          <Box mb={3}>
            <TextInput
              id="sign-in-email"
              name="username"
              type="email"
              label={l10n.auth.email}
              fullWidth
            />
          </Box>

          <Box position="relative">
            <PasswordInput
              id="sign-in-password"
              name="password"
              label={l10n.auth.password}
              fullWidth
            />
            <Box className={classes.forgotPassword} position="absolute">
              <Link to={ROUTING_CONFIG.resetPassword}>
                {l10n.auth.forgotPassword}
              </Link>
            </Box>
          </Box>

          <Box textAlign="center" mt={7.5}>
            <Button
              fullWidth
              loading={formik.isSubmitting}
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              className={classes.button}
            >
              {l10n.auth.signIn}
            </Button>
            {errorDetails && (
              <Box className={classes.errorAlert}>
                <AuthAlert severity="error" title={errorDetails.title} showIcon={false}>{errorDetails.details}</AuthAlert>
              </Box>
            )}
          </Box>

        </Form>
      )}
    </Formik>
  );
};
